/**
 * Workspace dashboard API calls (Bearer from auth session).
 * `X-Workspace-Id` must be set from workspace context (`/me` / list), not arbitrary input (SECURITY.md §2.6).
 */

import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { kongBrowserFetchUrl } from "@/lib/kongApi";

const TUD_PREFIX = "/api/v1/tenant-user-dashboard";

let _workspaceId = null;

/** @param {string | null} id */
export function setTenantApiWorkspaceId(id) {
  _workspaceId = id && String(id).trim() ? String(id).trim() : null;
}

/** Active workspace id for direct Kong calls (e.g. multipart KB upload). */
export function getTenantApiWorkspaceId() {
  return _workspaceId;
}

/**
 * @param {string} path - absolute path starting with /api/...
 * @param {RequestInit & { timeoutMs?: number }} [init]
 */
export async function tenantApiFetch(path, init = {}) {
  const { timeoutMs, headers: initHeaders, ...rest } = init;
  const url = kongBrowserFetchUrl(path);
  const buildRequest = () => {
    const headers = {
      Accept: "application/json",
      ...(typeof window !== "undefined" ? getBearerAuthHeaders() : {}),
      ...(initHeaders || {}),
    };
    if (_workspaceId) {
      headers["X-Workspace-Id"] = _workspaceId;
    }
    if (rest.body && typeof rest.body === "string" && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }
    return fetchWithTimeout(url, { ...rest, headers }, timeoutMs);
  };

  const res = await fetchWithAuthRetry(buildRequest);
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    const detail = data?.detail;
    const msg =
      (detail && typeof detail === "object" && detail.message) ||
      (typeof detail === "string" ? detail : null) ||
      data?.message ||
      (detail && typeof detail === "object" && detail.error) ||
      data?.error ||
      res.statusText;
    const err = new Error(typeof msg === "string" ? msg : JSON.stringify(msg || "Request failed"));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export function tenantDashboardPath(suffix) {
  return `${TUD_PREFIX}${suffix.startsWith("/") ? suffix : `/${suffix}`}`;
}
